/**
 * Texte, Fachstatus und Rechenstand der Seite „Erzeugung und Absicherung“
 * (TH-2, KG 420) – rein, ohne React, damit Seite und Test dieselben Regeln
 * lesen (TX-STANDARD S1.5).
 *
 * Nichts hier entscheidet etwas: Die Erzeugerbilanz (für 8000 heute 55,7 %)
 * steht unter dem Vorbehalt HZK-29, Kälteerzeuger nach DIN EN 378 sind
 * „nicht prüfbar“ (HZK-31/HZK-40). Die Texte dazu baut der Server in das
 * Datenmodell (eine Stelle für Seite und Excel).
 */
import type { HeizungErzeugungAntwort } from '../../api';
import { entfaellt, pruefergebnis, type Fachstatus } from '../../muster';
import { zeitpunkt } from './kaelte';

/**
 * Beschriftung der Linie „installierte Leistung“ in der Lastdauerlinie.
 * Auflage main zu TH-2: Der Jahresgang ist eine Stundenbilanz, KEIN
 * Auslegungsfall – die Linie darf nicht „gedeckt“ suggerieren.
 */
export const GRENZE_INSTALLIERT = 'installierte WT-Leistung; Auslegungsfall siehe Erzeugerbilanz';

/** Der Satz über der Lastdauerlinie: Auslegungsgröße daneben (Auflage main). */
export function auslegungssatz(erforderlichKw: number | null, installiertKw: number | null,
  deckungProzent: number | null, format: (x: number | null, n: number) => string): string {
  return `Auslegungsgröße: Normheizlast und Trinkwasser ${format(erforderlichKw, 1)} kW gegen `
    + `${format(installiertKw, 1)} kW installiert, Deckungsgrad ${format(deckungProzent, 1)} % `
    + '(Vorbehalt HZK-29, nicht entschieden).';
}

/** Bewertung der Erzeugerbilanz → Fachstatus. Nie „gut“ auf einer unteren Schranke. */
export function zustandBilanz(bewertung: string, vollstaendigkeit: string | null): Fachstatus {
  switch (bewertung) {
    case 'gedeckt':
      return vollstaendigkeit === 'untere_schranke'
        ? { zustand: 'warnung', text: 'gedeckt (untere Schranke)' }
        : { zustand: 'gut', text: 'gedeckt' };
    case 'knapp': return { zustand: 'warnung', text: 'knapp' };
    case 'unterdeckt': return { zustand: 'fehler', text: 'unterdeckt' };
    default: return { zustand: 'warnung', text: 'nicht beurteilbar' };
  }
}

/** Ergebnis der Absicherungsprüfung (J15) → Fachstatus; „entfällt“ immer mit Grund. */
export function zustandPruefung(stand: string, grund: string): Fachstatus & { titel?: string } {
  switch (stand) {
    case 'erfuellt': return pruefergebnis('erfuellt');
    case 'nicht_erfuellt': return pruefergebnis('nicht_erfuellt');
    case 'nicht_pruefbar': return pruefergebnis('nicht_pruefbar');
    case 'entfaellt': return entfaellt(grund.trim() === '' ? 'Regel trifft nicht zu' : grund);
    default: return pruefergebnis('hinweis');
  }
}

/** Bewertung der Ausdehnung je Gruppe → Fachstatus. */
export function zustandAusdehnung(bewertung: string): Fachstatus {
  switch (bewertung) {
    case 'gedeckt': return { zustand: 'gut', text: 'gedeckt' };
    case 'zu_klein': return { zustand: 'fehler', text: 'Gefäß zu klein' };
    case 'kein_gefaess': return { zustand: 'warnung', text: 'kein Gefäß im Modell' };
    case 'ohne_nennvolumen': return { zustand: 'warnung', text: 'ohne Nennvolumen' };
    default: return { zustand: 'warnung', text: 'nicht auslegbar' };
  }
}

/** Bewertung der Absicherung je Erzeuger → Fachstatus. */
export function zustandAbsicherung(bewertung: string): Fachstatus {
  switch (bewertung) {
    case 'gedeckt': return { zustand: 'gut', text: 'gedeckt (Tabelle)' };
    case 'einzelnachweis': return { zustand: 'warnung', text: 'Einzelnachweis nötig' };
    case 'ohne_leistung': return { zustand: 'warnung', text: 'ohne Leistung' };
    case 'nicht_en12828': return { zustand: 'neutral', text: 'nicht DIN EN 12828' };
    default: return { zustand: 'warnung', text: bewertung.replace(/_/g, ' ') };
  }
}

/** Bewertung eines Erzeugers in der Erzeugerauslegung → Fachstatus. */
export function zustandErzeuger(bewertung: string): Fachstatus {
  switch (bewertung) {
    case 'gerechnet': return { zustand: 'neutral', text: 'gerechnet' };
    case 'nur_liste': return { zustand: 'warnung', text: 'nur Geräteliste' };
    case 'ohne_leistung': return { zustand: 'warnung', text: 'ohne Leistung' };
    case 'nicht_en12828': return { zustand: 'neutral', text: 'nicht DIN EN 12828' };
    default: return { zustand: 'warnung', text: 'nicht beurteilbar' };
  }
}

/** Herkunft der Gleichzeitigkeit bzw. des Trinkwasseranteils als Klartext. */
export const HERKUNFT: Record<string, string> = {
  keine: 'keine festgelegt',
  ansatz: 'Ansatz',
  festlegung: 'festgelegt',
  friwa: 'Frischwasserstationen',
  modell: 'Modell',
  geraeteliste: 'Geräteliste',
  gemischt: 'Modell und Geräteliste',
  bauteilwert: 'Modell (Bauteilwert)',
};

/**
 * Rechenstand nach S1.2: „Heizlast Lauf 45 · gerechnet 13.10.2026 07:20 ·
 * J15 14.10.2026 11:19“; ohne jeden Lauf „noch nicht gerechnet“.
 */
export function rechenstandErzeugung(a: HeizungErzeugungAntwort): string {
  const s = a.stand;
  if (!a.modell && !s.erzeugerAm && !s.pruefungAm) return 'noch nicht gerechnet';
  const teile: string[] = [];
  if (a.modell?.heizlastLaufnummer != null) teile.push(`Heizlast Lauf ${a.modell.heizlastLaufnummer}`);
  if (s.erzeugerAm) teile.push(`gerechnet ${zeitpunkt(s.erzeugerAm)}`);
  if (s.ausdehnungAm) teile.push(`Ausdehnung ${zeitpunkt(s.ausdehnungAm)}`);
  if (s.pruefungAm) teile.push(`J15 ${zeitpunkt(s.pruefungAm)}`);
  return teile.join(' · ');
}

/** Freigabestatus des Datenmodells → Fachstatus (Vorabzug | Zwischenstand). */
export function zustandFreigabe(status: string): Fachstatus {
  return status === 'Zwischenstand'
    ? { zustand: 'info', text: 'Zwischenstand' }
    : { zustand: 'warnung', text: 'Vorabzug' };
}
